import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import USBInput from '@usb-shield/react-input'
import { isNullOrEmpty } from '../../../utils/Utility'

/**
 * Returns password input field (usbinput)
 * Customized prime react's password component to add our logic for password strength
 * @param {object} fieldSchema - schema of field
 * @param {object} fieldState - Any object, same shape as useUserManagerFieldsHook initialState
 * @param {func} setFieldState - function to set the (fieldState) state.
 * @param {func} onFocusHandler - optional onFocusHandler event.
 * @param {func} onBlurHandler - optional onBlurHandler event.
 * @param {func} onKeyUpHandler - optional onKeyUpHandler event.
 * @param {func} onChangeHandler - optional onChangeHandler event.
 * @param {bool} showCredentialStrength - optional - shows credential strength - defaults to false.
 * @param {string} weakLabel - optional - label for weak password - defaults to Weak.
 * @param {string} mediumLabel - optional - label for medium password - defaults to Medium.
 * @param {string} strongLabel - optional - label for strong password - defaults to Strong.
 * @param {bool} alwaysShowWeakLabel - optional - shows password strength until its not weak - defaults to false.
 * @param {bool} alwaysShowNotMatched - optional - shows if password and compareTo is not matched - defaults to false.
 * @param {string} compareTo - optional - text to compareTo the value entered (example: compare password with confirm password).
 */
const PrimeCredentialInput = ({
  fieldSchema,
  fieldState,
  setFieldState,
  onSensitiveValueChange,
  onFocusHandler,
  onBlurHandler,
  onKeyUpHandler,
  onChangeHandler,
  showCredentialStrength,
  weakLabel,
  mediumLabel,
  strongLabel,
  alwaysShowWeakLabel,
  alwaysShowNotMatched,
  compareTo,
  getCompareToValue,
  resetCounter,
  addClasses
}) => {
  const inputEl = useRef()
  const [
    state = {
      showMeter: false,
      meterClassName: '',
      meterPos: '',
      meterText: '',
      meterWidth: '200px'
    },
    setOriginalState
  ] = useState()

  const setState = useCallback(currState => {
    setOriginalState(prevState => ({ ...prevState, ...currState }))
  }, [])

  const getInputElement = () =>
    inputEl.current ? inputEl.current.getElementsByTagName('input')[0] : null
  const inputInstanceKey = `${fieldSchema.id}-${resetCounter}`

  /**
   * When field value is empty, will hide the stregth meter
   */
  useEffect(() => {
    const input = getInputElement()

    if (input) {
      input.value = ''
    }

    setState({
      showMeter: false,
      meterClassName: '',
      meterPos: '',
      meterText: ''
    })
  }, [resetCounter, setState])

  const meterWidth = inputEl.current ? inputEl.current.offsetWidth : 0
  useEffect(() => {
    if (inputEl.current) {
      setState({ meterWidth })
    }
  }, [meterWidth])

  const normalize = (x, y) => {
    const diff = x - y

    if (diff <= 0) return x / y
    return 1 + 0.5 * (x / (x + y / 4))
  }

  const testStrength = str => {
    let grade = 0
    let val

    val = str.match('[0-9]')
    grade += normalize(val ? val.length : 1 / 4, 1) * 25

    val = str.match('[a-zA-Z]')
    grade += normalize(val ? val.length : 1 / 2, 3) * 10

    val = str.match('[!@#$%^&*?_~.,;=]')
    grade += normalize(val ? val.length : 1 / 6, 1) * 35

    val = str.match('[A-Z]')
    grade += normalize(val ? val.length : 1 / 6, 1) * 30

    grade *= str.length / 8

    return grade > 100 ? 100 : grade
  }

  const onFocus = e => {
    // set state value, so we can show and hide helper text
    setFieldState(fieldSchema && fieldSchema.id, { isFocused: true })

    // show the meter if value is not empty
    if (showCredentialStrength) {
      setState({ showMeter: e.target.value.trim() !== '' })
    }

    if (onFocusHandler) {
      onFocusHandler(e)
    }
  }

  /**
   * When password field loses focus, we hide the meter, but if alwaysShowWeakLabel or alwaysShowNotMatched is true
   * and meterText is either Weak or Not Matched, then we do not hide
   */
  const onBlur = e => {
    if (showCredentialStrength) {
      if (
        e.target.value !== '' &&
        ((alwaysShowWeakLabel && isNullOrEmpty(state.meterText) === 'Weak') ||
          (alwaysShowNotMatched && isNullOrEmpty(state.meterText) === 'Not Matched'))
      ) {
        // do nothing
      } else {
        setTimeout(() => {
          setState({ showMeter: false })
        }, 150)
      }
    }

    if (onBlurHandler) {
      onBlurHandler(e)
    }
    if (setFieldState) {
      setFieldState(fieldSchema.id, { isFocused: false })
      if (Object.prototype.hasOwnProperty.call(fieldState, 'isPasswordMismatch') && e.target.value === '')
        setFieldState(fieldSchema.id, {
          isPasswordMismatch: false,
          isFocused: false
        })
    }
  }

  const validateInputValue = inputValue => {
    if (showCredentialStrength) {
      const value = inputValue || ''
      let label = null
      let meterPos = null
      const compareValue = getCompareToValue ? getCompareToValue() : compareTo

      if (value.length > 0) {
        if (compareValue) {
          if (compareValue === value) {
            label = 'Matched'
            meterPos = '0px -30px'
          } else if (value.length > 5 || value.length >= compareValue.length) {
            label = 'Not Matched'
            meterPos = '0px -10px'
          }
        } else {
          const score = testStrength(value)

          if (score < 30) {
            label = weakLabel
            meterPos = '0px -10px'
          } else if (score >= 30 && score < 80) {
            label = mediumLabel
            meterPos = '0px -20px'
          } else if (score >= 80) {
            label = strongLabel
            meterPos = '0px -30px'
          }
        }
      }

      setState({
        meterPos,
        meterText: label,
        showMeter: value.trim().length > 1 && label !== null
      })
    }
  }
  const onKeyup = e => {
    validateInputValue(e.target.value)
    if (onKeyUpHandler) {
      onKeyUpHandler(e)
    }
  }

  /**
   * Updates the state to, value from input textbox
   */
  const onChange = e => {
    if (setFieldState) {
      setFieldState(fieldSchema.id, {
        changed: e.target.value.trim().length > 0,
        isError: false,
        isPasswordMismatch: false,
        isUserExistsError: false
      })
    }
    if (onSensitiveValueChange) {
      onSensitiveValueChange(fieldSchema.id, e.target.value)
    }
    validateInputValue(e.target.value)
    if (onChangeHandler) {
      // raise event given from props
      onChangeHandler(e)
    }
  }

  let showHide = {}

  if (fieldSchema && fieldSchema.type === 'password')
    showHide = {
      isShowHide: true,
      showBtnLabel: 'show',
      hideBtnLabel: 'hide'
    }

  const getErrorText = () => {
    const { errorText, userExistError, pwdMismatchErrorText } = fieldSchema
    const { isPasswordMismatch, isUserExistsError } = fieldState
    if (isUserExistsError) return userExistError
    if (isPasswordMismatch) return pwdMismatchErrorText
    return errorText
  }

  /**
   * We want to show the helper text on when the field is active
   * When field is marked as not required and has error text present, then return error text, else return Optional
   * When field is readOnly/disabled then return Read Only
   */
  const getHelperText = () => {
    const { errorText } = fieldSchema
    const { isFocused } = fieldState

    return isFocused ? errorText : ''
  }

  return (
    <div style={{ width: '100%' }}>
      <USBInput
        key={inputInstanceKey}
        // eslint-disable-next-line react/no-find-dom-node
        forwardedRef={inputEl}
        inputId={fieldSchema.id}
        name={fieldSchema.id}
        type="text"
        maxLength={fieldSchema.maxLength}
        labelText={fieldSchema.label}
        errorText={getErrorText()}
        isError={fieldState.isError}
        handleKeyPress={onKeyup}
        handleBlur={onBlur}
        handleFocus={onFocus}
        handleChange={onChange}
        defaultValue=""
        helperText={getHelperText()}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...showHide}
        addClasses={`input-border ${addClasses}`}
      />

      {state && state.showMeter && (
        <div
          className="p-password-panel p-component p-highlight passwordfeedback"
          style={{ minWidth: state.meterWidth }}
        >
          <div className="p-password-meter" style={{ backgroundPosition: state.meterPos }} />
          <div className="p-password-info">{state.meterText}</div>
        </div>
      )}
    </div>
  )
}

PrimeCredentialInput.propTypes = {
  promptLabel: PropTypes.string,
  weakLabel: PropTypes.string,
  mediumLabel: PropTypes.string,
  strongLabel: PropTypes.string,
  addClasses: PropTypes.string,
  compareTo: PropTypes.string,
  showCredentialStrength: PropTypes.bool,
  alwaysShowWeakLabel: PropTypes.bool,
  alwaysShowNotMatched: PropTypes.bool,
  fieldSchema: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    minLength: PropTypes.number.isRequired,
    maxLength: PropTypes.number.isRequired,
    pattern: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(RegExp)]),
    errorText: PropTypes.string.isRequired,
    userExistError: PropTypes.string,
    pwdMismatchErrorText: PropTypes.string,
    isRequired: PropTypes.bool.isRequired
  }).isRequired,

  fieldState: PropTypes.shape({
    id: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    isError: PropTypes.bool.isRequired,
    isUserExistsError: PropTypes.bool,
    isFocused: PropTypes.bool.isRequired,
    isPasswordMismatch: PropTypes.bool
  }).isRequired,

  setFieldState: PropTypes.func,
  onSensitiveValueChange: PropTypes.func,
  onFocusHandler: PropTypes.func,
  onBlurHandler: PropTypes.func,
  onKeyUpHandler: PropTypes.func,
  onChangeHandler: PropTypes.func,
  getCompareToValue: PropTypes.func,
  resetCounter: PropTypes.number
}
PrimeCredentialInput.defaultProps = {
  promptLabel: 'Enter a password',
  weakLabel: 'Weak',
  mediumLabel: 'Medium',
  strongLabel: 'Strong',
  compareTo: '',
  showCredentialStrength: false,
  alwaysShowWeakLabel: false,
  alwaysShowNotMatched: false,
  onBlurHandler: null,
  onChangeHandler: null,
  onKeyUpHandler: null,
  onFocusHandler: null,
  onSensitiveValueChange: null,
  setFieldState: null,
  getCompareToValue: null,
  resetCounter: 0,
  addClasses: ''
}

export default PrimeCredentialInput
